import { useCallback, useEffect, useRef, useState } from 'react';
import { calcularRangeDiaBRT } from '@/shared/lib/dateUtils';
import { useReacaoReceptor } from '@/features/jukebox';
import { engajamentoRepository } from '../../data/engajamentoRepository';
import type { TvEngajamentoState } from '../../domain/entities/Engajamento';

const REFRESH_MS = 60_000;
const LS_PREFIX  = 'ds:reacoes_dia:';

function chaveReacoesDia(telaId: string): string {
  const { dataBRT } = calcularRangeDiaBRT();
  return `${LS_PREFIX}${telaId}:${dataBRT}`;
}

function lerReacoesDia(telaId: string): number {
  try {
    const raw = localStorage.getItem(chaveReacoesDia(telaId));
    return raw ? Math.max(0, parseInt(raw, 10) || 0) : 0;
  } catch {
    return 0;
  }
}

function incrementarReacoesDia(telaId: string): number {
  const key = chaveReacoesDia(telaId);
  const next = lerReacoesDia(telaId) + 1;
  try {
    localStorage.setItem(key, String(next));
  } catch { /* quota / modo privado */ }
  return next;
}

const ESTADO_VAZIO: TvEngajamentoState = {
  pedidos: 0,
  reacoes: 0,
  votos:   0,
  ranking: [],
};

/**
 * Agrega pedidos, votos, ranking (RPC) e reações do dia (broadcast + localStorage).
 * Refresh a cada 60s. O INSERT da fila chega pelo Player (`sinalInsercaoFila`),
 * que já assina o broadcast privado — este hook não abre canal de fila.
 */
export function useTvEngajamento(
  telaId: string | undefined,
  habilitado: boolean,
  sinalInsercaoFila = 0,
): TvEngajamentoState {
  const [estado, setEstado] = useState<TvEngajamentoState>(ESTADO_VAZIO);
  const habilitadoRef = useRef(habilitado);
  useEffect(() => { habilitadoRef.current = habilitado; });

  const carregar = useCallback(async () => {
    if (!telaId || !habilitadoRef.current) return;
    try {
      const [contadores, ranking] = await Promise.all([
        engajamentoRepository.buscarContadoresDia(telaId),
        engajamentoRepository.buscarRankingDia(telaId),
      ]);
      setEstado({
        pedidos: contadores.pedidos,
        votos:   contadores.votos,
        reacoes: lerReacoesDia(telaId),
        ranking,
      });
    } catch (err) {
      console.warn('[TvEngajamento] falha ao carregar:', err);
    }
  }, [telaId]);

  useEffect(() => {
    if (!telaId || !habilitado) {
      setEstado(ESTADO_VAZIO);
      return;
    }
    void carregar();
    const interval = setInterval(() => { void carregar(); }, REFRESH_MS);
    return () => clearInterval(interval);
  }, [telaId, habilitado, carregar]);

  useEffect(() => {
    if (!telaId || !habilitado || sinalInsercaoFila === 0) return;
    void carregar();
  }, [telaId, habilitado, sinalInsercaoFila, carregar]);

  const onReacao = useCallback(() => {
    if (!telaId) return;
    const reacoes = incrementarReacoesDia(telaId);
    setEstado(prev => ({ ...prev, reacoes }));
  }, [telaId]);

  useReacaoReceptor(habilitado ? telaId : null, onReacao);

  return estado;
}
